import React, { useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { ClientJobPreviewList } from "./client-job-preview-list.component";
import { useRecentClientJobs } from "@/dentlab/src/hooks/useRecentClientJobs";
import { PlusCircle, Loader2 } from "lucide-react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { Logger } from "@/lib/logger/Logger";

interface ClientPreviewHoverCardProps {
	children: React.ReactNode;
	clientId: string;
	onJobCreated?: (jobId: number) => void;
	onJobSelect?: (jobId: number) => void;
}

export const ClientPreviewHoverCard: React.FC<ClientPreviewHoverCardProps> = ({
	children,
	clientId,
	onJobCreated,
	onJobSelect,
}) => {
	const [open, setOpen] = useState(false);

	return (
		<HoverCard
			openDelay={300}
			closeDelay={100}
			open={open}
			onOpenChange={setOpen}
		>
			<HoverCardTrigger asChild>
				{children}
			</HoverCardTrigger>
			<HoverCardContent
				side="right"
				align="start"
				className="w-80 p-0 rounded-lg bg-slate-100 -mt-2"
			>
				{/* We only render the data-fetching component when the hover card is open, to reduce the number of requests 
				(otherwise on loading the page or opening the sidebar, we would make a request for every menu item)
				*/}
				{open && (
					<HoverCardContentWithData
						clientId={clientId}
						onJobCreated={onJobCreated}
						onJobSelect={onJobSelect}
						onClose={() => setOpen(false)}
					/>
				)}
			</HoverCardContent>
		</HoverCard>
	);
};

const HoverCardContentWithData: React.FC<{
	clientId: string;
	onJobCreated?: (jobId: number) => void;
	onJobSelect?: (jobId: number) => void;
	onClose: () => void;
}> = ({ clientId, onJobCreated, onJobSelect, onClose }) => {
	const [isCreatingJob, setIsCreatingJob] = useState(false);
	const createJobOutsideOfClient = useJobStore((state) => state.createJobOutsideOfClient);
	const { jobs, loading } = useRecentClientJobs(clientId, 5);

	const handleCreateJob = async () => {
		setIsCreatingJob(true);
		try {
			const job = await createJobOutsideOfClient(clientId);
			if (job && job.id) {
				onClose();
				onJobCreated?.(job.id);
			}
		} catch (error) {
			Logger.error("Error creating job from hover card: " + error);
		} finally {
			setIsCreatingJob(false);
		}
	};

	const handleJobSelect = (jobId: number) => {
		onClose();
		onJobSelect?.(jobId);
	};

	return (
		<>
			<button
				onClick={handleCreateJob}
				disabled={isCreatingJob}
				className="w-full rounded-t-lg flex items-center justify-center gap-2 px-3 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium transition-colors border-b border-white"
				aria-label="Neuer Auftrag"
			>
				{isCreatingJob ? (
					<Loader2 size={16} className="animate-spin" />
				) : (
					<PlusCircle size={16} />
				)}
				<span>Neuer Auftrag</span>
			</button>
			<div className="max-h-[350px] overflow-y-auto rounded-b-lg">
				{loading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 size={24} className="animate-spin text-gray-400" />
					</div>
				) : (
					<ClientJobPreviewList
						jobs={jobs}
						onJobSelect={handleJobSelect}
					/>
				)}
			</div>
		</>
	);
};